import { useState, useCallback, useRef } from 'react';
import useNotification from './useNotification';

/**
 * Configuración para una acción optimista
 *
 * @template TData - Tipo de datos de entrada para la acción
 * @template TResult - Tipo de resultado esperado de la acción
 * @template TContext - Tipo de contexto para rollback (opcional)
 */
export interface OptimisticActionConfig<TData, TResult, TContext = any> {
  /**
   * Función que ejecuta la acción en el servidor
   * Debe retornar una Promise con el resultado
   */
  action: (data: TData) => Promise<TResult>;

  /**
   * Callback ejecutado inmediatamente antes de llamar al servidor (optimistic update)
   * Usa esto para actualizar la UI de forma optimista
   *
   * @param data - Datos de la acción
   * @param context - Contexto generado por getContext (útil para guardar estado pre-acción)
   */
  onOptimisticStart?: (data: TData, context?: TContext) => void;

  /**
   * Callback ejecutado cuando la acción es exitosa (confirmación del optimistic update)
   * La UI ya está actualizada, usa esto para acciones adicionales
   *
   * @param result - Resultado de la acción del servidor
   * @param data - Datos originales de la acción
   * @param context - Contexto de rollback
   */
  onSuccess?: (result: TResult, data: TData, context?: TContext) => void;

  /**
   * Callback ejecutado cuando la acción falla (rollback del optimistic update)
   * Usa esto para revertir cambios de UI y restaurar estado previo
   *
   * @param error - Error recibido del servidor
   * @param data - Datos originales de la acción
   * @param context - Contexto con snapshot del estado pre-acción
   */
  onError?: (error: any, data: TData, context?: TContext) => void;

  /**
   * Callback que siempre se ejecuta al finalizar (éxito o error)
   * Similar a finally en try/catch
   *
   * @param result - Resultado si éxito, undefined si error
   * @param error - Error si falla, null si éxito
   * @param data - Datos originales de la acción
   * @param context - Contexto de rollback
   */
  onSettled?: (
    result: TResult | undefined,
    error: any,
    data: TData,
    context?: TContext
  ) => void;

  /**
   * Función para generar contexto de rollback antes de la acción optimista
   * Usa esto para guardar snapshots del estado que necesitarás restaurar en caso de error
   *
   * @param data - Datos de la acción
   * @returns Contexto que se pasará a todos los callbacks
   */
  getContext?: (data: TData) => TContext;

  /**
   * Opciones adicionales para configurar comportamiento
   */
  options?: {
    /**
     * Número de reintentos automáticos en caso de fallo
     * @default 0
     */
    retry?: number;

    /**
     * Delay en milisegundos entre reintentos
     * @default 1000
     */
    retryDelay?: number;

    /**
     * Habilitar o deshabilitar optimistic updates
     * Si es false, se comporta como una acción async normal
     * @default true
     */
    optimistic?: boolean;

    /**
     * Mostrar mensajes de toast automáticos
     * @default true
     */
    showMessages?: boolean;

    /**
     * Mensajes personalizados para los diferentes estados
     */
    messages?: {
      /**
       * Mensaje mientras la acción está pendiente
       */
      pending?: string;

      /**
       * Mensaje de éxito (puede ser función para mensajes dinámicos)
       */
      success?: string | ((result: TResult) => string);

      /**
       * Mensaje de error (puede ser función para mensajes dinámicos)
       */
      error?: string | ((error: any) => string);
    };
  };
}

/**
 * Estado interno del hook
 */
interface OptimisticActionState {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: any;
  data: any;
}

/**
 * Hook genérico para implementar Optimistic UI pattern
 *
 * Permite ejecutar acciones asíncronas con feedback UI instantáneo,
 * asumiendo que la operación será exitosa (optimistic).
 * Si la operación falla, automáticamente hace rollback del estado.
 *
 * @example
 * ```typescript
 * const createOfferAction = useOptimisticAction({
 *   action: (offerData) => travelerService.createOffer(offerData),
 *   onOptimisticStart: (data, context) => {
 *     removeOrder(order);
 *     handleClose();
 *   },
 *   onError: (error, data, context) => {
 *     restoreOrder(context.orderBackup);
 *   },
 *   getContext: () => ({ orderBackup: order }),
 *   options: {
 *     messages: {
 *       pending: 'Enviando oferta...',
 *       success: 'Oferta enviada exitosamente',
 *     }
 *   }
 * });
 *
 * // Ejecutar la acción
 * createOfferAction.execute(offerData);
 * ```
 *
 * @template TData - Tipo de datos de entrada
 * @template TResult - Tipo de resultado esperado
 * @template TContext - Tipo de contexto para rollback
 *
 * @param config - Configuración de la acción optimista
 * @returns Objeto con función execute, reset, y estado de la acción
 */
export function useOptimisticAction<TData, TResult, TContext = any>(
  config: OptimisticActionConfig<TData, TResult, TContext>
) {
  // Usar sistema de toasts V2 con queue
  const { createMessage } = useNotification();

  const [state, setState] = useState<OptimisticActionState>({
    isLoading: false,
    isError: false,
    isSuccess: false,
    error: null,
    data: null,
  });

  // Track de acciones pendientes para evitar duplicados
  const pendingActionsRef = useRef<Set<string>>(new Set());

  /**
   * Ejecuta la acción optimista
   *
   * @param data - Datos para la acción
   * @param actionId - ID opcional para tracking (previene duplicados)
   * @returns Promise con el resultado de la acción
   */
  const execute = useCallback(
    async (data: TData, actionId?: string) => {
      // Generar ID único para esta acción
      const id = actionId || `action-${Date.now()}-${Math.random()}`;

      // Evitar duplicados
      if (pendingActionsRef.current.has(id)) {
        console.warn(`Action ${id} already pending`);
        return;
      }

      // Marcar como pendiente
      pendingActionsRef.current.add(id);

      // Generar contexto para rollback (snapshot del estado actual)
      const context = config.getContext?.(data);

      // Estado inicial
      setState({
        isLoading: true,
        isError: false,
        isSuccess: false,
        error: null,
        data: null,
      });

      // 1. OPTIMISTIC UPDATE (inmediato)
      if (config.options?.optimistic !== false) {
        config.onOptimisticStart?.(data, context);

        // Mensaje de feedback suave con key para reemplazo
        if (config.options?.showMessages !== false) {
          const pendingMsg = config.options?.messages?.pending || 'Procesando...';
          createMessage.info(pendingMsg, id); // ← Key para reemplazar después
        }
      }

      // 2. EJECUTAR ACCIÓN EN BACKGROUND
      try {
        // Función helper para retry
        const executeWithRetry = async (retriesLeft: number): Promise<TResult> => {
          try {
            return await config.action(data);
          } catch (error) {
            if (retriesLeft > 0) {
              const delay = config.options?.retryDelay || 1000;
              await new Promise((resolve) => setTimeout(resolve, delay));
              return executeWithRetry(retriesLeft - 1);
            }
            throw error;
          }
        };

        const retries = config.options?.retry || 0;
        const result = await executeWithRetry(retries);

        // Verificar si es un error del servidor (aunque no lance excepción)
        const hasError = (result as any)?.error || (result as any)?.info?.error;

        if (hasError) {
          throw new Error(
            (result as any)?.message ||
              (result as any)?.info?.message ||
              'Error en la operación'
          );
        }

        // 3. SUCCESS - Confirmar optimistic update
        setState({
          isLoading: false,
          isError: false,
          isSuccess: true,
          error: null,
          data: result,
        });

        // Callback de éxito
        config.onSuccess?.(result, data, context);

        // Mensaje de éxito (reemplaza toast pendiente)
        if (config.options?.showMessages !== false) {
          const successMsg =
            typeof config.options?.messages?.success === 'function'
              ? config.options.messages.success(result)
              : config.options?.messages?.success || 'Operación exitosa';
          createMessage.success(successMsg, id); // ← Mismo key, reemplaza el toast "Procesando..."
        }

        // Callback settled
        config.onSettled?.(result, null, data, context);

        return result;
      } catch (error) {
        // 4. ERROR - Rollback
        setState({
          isLoading: false,
          isError: true,
          isSuccess: false,
          error,
          data: null,
        });

        // Callback de error (rollback)
        config.onError?.(error, data, context);

        // Mensaje de error (reemplaza toast pendiente)
        if (config.options?.showMessages !== false) {
          const errorMsg =
            typeof config.options?.messages?.error === 'function'
              ? config.options.messages.error(error)
              : config.options?.messages?.error ||
                (error as any)?.message ||
                'Error en la operación';
          createMessage.error(errorMsg, id); // ← Mismo key, reemplaza el toast "Procesando..."
        }

        // Callback settled
        config.onSettled?.(undefined, error, data, context);

        throw error;
      } finally {
        // Remover de pendientes
        pendingActionsRef.current.delete(id);
      }
    },
    [config, createMessage]
  );

  /**
   * Reset manual del estado
   * Útil si necesitas limpiar el estado entre acciones
   */
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      isError: false,
      isSuccess: false,
      error: null,
      data: null,
    });
  }, []);

  return {
    /**
     * Ejecuta la acción optimista
     */
    execute,

    /**
     * Resetea el estado del hook
     */
    reset,

    /**
     * Indica si la acción está en progreso
     */
    isLoading: state.isLoading,

    /**
     * Indica si la acción terminó con error
     */
    isError: state.isError,

    /**
     * Indica si la acción terminó exitosamente
     */
    isSuccess: state.isSuccess,

    /**
     * Error de la acción (si hubo)
     */
    error: state.error,

    /**
     * Resultado de la acción (si fue exitosa)
     */
    data: state.data,

    /**
     * Indica si hay acciones pendientes
     * Útil para mostrar loaders globales
     */
    isPending: pendingActionsRef.current.size > 0,
  };
}

// Export helper type para inferencia de tipos
export type UseOptimisticAction = typeof useOptimisticAction;
